import { useEffect, useMemo, useRef, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Toolbar from '@mui/material/Toolbar'
import { DateTimePicker, type DateTimePickerProps, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import type { DateTimeValidationError } from '@mui/x-date-pickers/models'
import { useSearchParams } from 'react-router'
import { UTCDate } from '@date-fns/utc'
import { AdapterDateFnsUTC } from './AdapterDateFnsUTC'

export enum FilterType {
    autocomplete = 'autocomplete',
    checkbox = 'checkbox',
    datetime = 'datetime',
    select = 'select',
    text = 'text',
}

export interface FilterOption {
    name: string
    value: string
}

interface BaseFilterProps {
    hidden?: boolean
    label: string
    paramName: string
}

export type FilterProps =
    | (BaseFilterProps & {
          type: FilterType.autocomplete
          loadOptions: (filter?: string) => Promise<FilterOption[]>
      })
    | (BaseFilterProps & {
          type: FilterType.checkbox
          defaultValue?: boolean
          labelPlacement?: 'bottom' | 'end' | 'start' | 'top'
      })
    | (BaseFilterProps & {
          type: FilterType.datetime
          maxDate?: Date
          maxDateMessage?: string
          minDate?: Date
          minDateMessage?: string
          timezone?: 'UTC' | 'system'
      })
    | (BaseFilterProps & {
          type: FilterType.select
          emptyOptionLabel?: string
          options?: FilterOption[]
      })
    | (BaseFilterProps & { type: FilterType.text })

interface FilterControlProps {
    pageParamName: string
    size: 'medium' | 'small'
}

interface FiltersProps extends Partial<FilterControlProps> {
    compact?: boolean
    filters: FilterProps[]
}

function useUrlFilter(paramName: string, pageParamName: string, defaultValue = '') {
    const [searchParams, setSearchParams] = useSearchParams()
    const searchParamsRef = useRef(searchParams)
    searchParamsRef.current = searchParams
    const value = searchParams.get(paramName) ?? defaultValue
    const setValue = (nextValue?: string) => {
        const nextSearchParams = new URLSearchParams(searchParamsRef.current)
        if (nextValue) nextSearchParams.set(paramName, nextValue)
        else nextSearchParams.delete(paramName)
        nextSearchParams.set(pageParamName, '0')
        setSearchParams(nextSearchParams, { replace: true })
    }

    return { setValue, value }
}

export const FilterSelect = ({
    emptyOptionLabel = 'None',
    label,
    options = [],
    pageParamName = 'pageNumber',
    paramName,
    size = 'medium',
}: Extract<FilterProps, { type: FilterType.select }> & Partial<FilterControlProps>) => {
    const { setValue, value } = useUrlFilter(paramName, pageParamName)
    const labelId = `${paramName}-filter-label`

    return (
        <FormControl size={size} sx={{ minWidth: 150 }}>
            <InputLabel id={labelId} shrink>
                {label}
            </InputLabel>
            <Select
                autoWidth
                displayEmpty
                labelId={labelId}
                value={value}
                onChange={({ target }) => setValue(String(target.value) || undefined)}
                label={label}
                renderValue={(selectedValue) =>
                    selectedValue
                        ? (options.find((option) => option.value === selectedValue)?.name ?? String(selectedValue))
                        : emptyOptionLabel
                }
            >
                <MenuItem value="">
                    <em>{emptyOptionLabel}</em>
                </MenuItem>
                {options.map((option) => (
                    <MenuItem value={option.value} key={option.value}>
                        {option.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}

const TextFilter = ({
    label,
    pageParamName,
    paramName,
    size,
}: Extract<FilterProps, { type: FilterType.text }> & FilterControlProps) => {
    const { setValue, value } = useUrlFilter(paramName, pageParamName)
    const [inputValue, setInputValue] = useState(value)
    const updateTimeout = useRef<ReturnType<typeof setTimeout>>(undefined)

    useEffect(() => {
        setInputValue(value)
        return () => clearTimeout(updateTimeout.current)
    }, [value])

    return (
        <TextField
            label={label}
            size={size}
            value={inputValue}
            onChange={({ target }) => {
                const nextValue = target.value
                setInputValue(nextValue)
                clearTimeout(updateTimeout.current)
                updateTimeout.current = setTimeout(() => setValue(nextValue || undefined), 500)
            }}
            sx={{ minWidth: 150 }}
        />
    )
}

const CheckboxFilter = ({
    defaultValue = false,
    label,
    labelPlacement = 'end',
    pageParamName,
    paramName,
}: Extract<FilterProps, { type: FilterType.checkbox }> & FilterControlProps) => {
    const { setValue, value } = useUrlFilter(paramName, pageParamName, defaultValue.toString())

    return (
        <FormControlLabel
            label={label}
            labelPlacement={labelPlacement}
            control={
                <Checkbox checked={value === 'true'} onChange={({ target }) => setValue(target.checked.toString())} />
            }
            slotProps={{ typography: { variant: 'body2' } }}
        />
    )
}

const DateFilter = ({
    label,
    maxDate = new Date(),
    maxDateMessage,
    minDate = new Date('2020-01-01'),
    minDateMessage,
    timezone = 'system',
    pageParamName,
    paramName,
    size,
}: Extract<FilterProps, { type: FilterType.datetime }> & FilterControlProps) => {
    const { setValue, value } = useUrlFilter(paramName, pageParamName)
    const [open, setOpen] = useState(false)
    const [error, setError] = useState<DateTimeValidationError | null>(null)
    const ampm = useMemo(
        () => new Intl.DateTimeFormat(navigator.languages, { hour: 'numeric' }).resolvedOptions().hour12 ?? false,
        []
    )

    const errorMessage = useMemo(() => {
        switch (error) {
            case 'disableFuture':
                return 'Future dates cannot be specified'
            case 'invalidDate':
                return 'Invalid date'
            case 'maxDate':
                return maxDateMessage ?? ''
            case 'minDate':
                return minDateMessage ?? ''
            default:
                return ''
        }
    }, [error, maxDateMessage, minDateMessage])

    const handleChange: DateTimePickerProps['onChange'] = (nextValue) => {
        if (nextValue === null) setValue()
        else if (!Number.isNaN(nextValue.getTime())) setValue(nextValue.toISOString())
    }

    return (
        <LocalizationProvider dateAdapter={timezone === 'UTC' ? AdapterDateFnsUTC : AdapterDateFns}>
            <DateTimePicker
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                label={label}
                ampm={ampm}
                format={ampm ? 'yyyy-MM-dd hh:mm a' : 'yyyy-MM-dd HH:mm'}
                value={value ? (timezone === 'UTC' ? new UTCDate(value) : new Date(value)) : null}
                onChange={handleChange}
                minDate={timezone === 'UTC' ? new UTCDate(minDate) : minDate}
                maxDate={timezone === 'UTC' ? new UTCDate(maxDate) : maxDate}
                disableFuture
                onError={setError}
                slotProps={{
                    actionBar: { actions: ['clear', 'accept'] },
                    textField: { helperText: errorMessage, onClick: () => setOpen(true), size },
                }}
            />
        </LocalizationProvider>
    )
}

const AutocompleteFilter = ({
    label,
    loadOptions,
    pageParamName,
    paramName,
    size,
}: Extract<FilterProps, { type: FilterType.autocomplete }> & FilterControlProps) => {
    const { setValue, value } = useUrlFilter(paramName, pageParamName)
    const [inputValue, setInputValue] = useState(value)
    const [searchText, setSearchText] = useState(value)
    const [selected, setSelected] = useState<FilterOption | null>(value ? { name: value, value } : null)
    const [options, setOptions] = useState<FilterOption[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [loadFailed, setLoadFailed] = useState(false)
    const initialized = useRef(false)
    const appliedValue = useRef(value)
    const loadOptionsRef = useRef(loadOptions)
    loadOptionsRef.current = loadOptions

    useEffect(() => {
        let active = true
        setIsLoading(true)
        const updateTimeout = setTimeout(
            () =>
                void loadOptionsRef
                    .current(searchText || undefined)
                    .then((nextOptions) => {
                        if (!active) return
                        setOptions(nextOptions)
                        setLoadFailed(false)
                    })
                    .catch(() => {
                        if (!active) return
                        setOptions([])
                        setLoadFailed(true)
                    })
                    .finally(() => {
                        if (!active) return
                        setIsLoading(false)
                        initialized.current = true
                    }),
            initialized.current ? 500 : 0
        )
        return () => {
            active = false
            clearTimeout(updateTimeout)
        }
    }, [searchText])

    useEffect(() => {
        if (value === appliedValue.current) return
        appliedValue.current = value
        if (value) {
            setSelected({ name: value, value })
            setInputValue(value)
        } else {
            setSelected(null)
            setInputValue('')
            setSearchText('')
        }
    }, [value])

    return (
        <Autocomplete
            value={selected}
            inputValue={inputValue}
            options={options}
            loading={isLoading}
            noOptionsText={loadFailed ? 'Failed to load options' : 'No options'}
            filterOptions={(availableOptions) => availableOptions}
            onChange={(_, option) => {
                appliedValue.current = option?.value ?? ''
                setSelected(option)
                setValue(option?.value)
                setInputValue(option?.name ?? '')
            }}
            onInputChange={(_, nextInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                    setInputValue(nextInputValue)
                    setSearchText(nextInputValue)
                } else if (reason === 'reset' || reason === 'blur') {
                    setInputValue(selected?.name ?? '')
                }
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
            renderInput={(params) => (
                <TextField
                    {...params}
                    name={paramName}
                    label={label}
                    size={size}
                    slotProps={{
                        ...params.slotProps,
                        htmlInput: { ...params.slotProps.htmlInput, autoComplete: 'off' },
                    }}
                />
            )}
            sx={{ minWidth: 240 }}
        />
    )
}

const FilterControl = ({ filter, pageParamName, size }: { filter: FilterProps } & FilterControlProps) => {
    const props = { ...filter, pageParamName, size }
    switch (filter.type) {
        case FilterType.autocomplete:
            return <AutocompleteFilter {...(props as Parameters<typeof AutocompleteFilter>[0])} />
        case FilterType.checkbox:
            return <CheckboxFilter {...(props as Parameters<typeof CheckboxFilter>[0])} />
        case FilterType.datetime:
            return <DateFilter {...(props as Parameters<typeof DateFilter>[0])} />
        case FilterType.select:
            return <FilterSelect {...(props as Parameters<typeof FilterSelect>[0])} />
        case FilterType.text:
            return <TextFilter {...(props as Parameters<typeof TextFilter>[0])} />
    }
}

const Filter = ({ compact = false, filters, pageParamName = 'pageNumber', size = 'medium' }: FiltersProps) => (
    <Toolbar
        disableGutters
        sx={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: compact ? 1 : 2,
            margin: compact ? 0 : (theme) => theme.spacing(1, 0, 2),
            minHeight: compact ? 'auto' : undefined,
        }}
    >
        {filters
            .filter((filter) => !filter.hidden)
            .map((filter) => (
                <FilterControl
                    key={`filter-${filter.paramName}`}
                    filter={filter}
                    pageParamName={pageParamName}
                    size={size}
                />
            ))}
    </Toolbar>
)

export default Filter
